import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NzModalRef, NzModalService } from 'ng-zorro-antd/modal';
import { NzMessageService } from 'ng-zorro-antd/message';
import { readFileAsDataURL } from '../../../../../util/read-file-as-data-url';
import { unsortedKeyValue } from '../../../../../util/unsorted-keyvalue.util';
import { ModalImageCropperComponent } from '../../../../../components/modal-image-cropper/modal-image-cropper.component';
import { generateImageFromAnother, GenerateImageFromAnotherConfig } from '../../../../../util/generate-image-from-another.util';
import { convertBase64ToBlob } from '../../../../../util/convert-base64-to-blob.util';
import { imageMaxSizeValidator } from '../../../../../validators/image-max-size.validator';
import { pickFile } from '../../../../../util/pick-file.util';
import { environment } from '@environment';

enum FieldsSections {
  WEB = 'WEB',
  MOBILE = 'MOBILE',
  STORE = 'STORE'
}

type FieldSectionConfig = {
  title: string;
  subtitle: string;
};

type DerivedImage = {
  imageFileName: string;
  imageBase64: string | null;
  imageFolder?: string;
  imageConfig: GenerateImageFromAnotherConfig;
};

type FieldConfig = {
  section: FieldsSections;
  label: string;
  helpText?: string;
  originalImageBase64: string | null;
  imageFileName: string;
  imageFolder?: string;
  isCropped: boolean;
  autoGenerateImage: boolean;
  awsBucketImagePath: string;
  imageConfig: GenerateImageFromAnotherConfig;
  derivedImages?: DerivedImage[] | null;
};

type WhitelabelImageKey = (typeof WHITELABEL_IMAGES)[number];

export type WhitelabelImages = {
  [TImageKey in WhitelabelImageKey]: Record<'imageBase64' | 'originalImageBase64' | 'imageFileName' | 'imageFolder', string | null> & {
    isCropped: boolean;
    derivedImages: DerivedImage[] | null;
  };
};

export type CreateWhitelabelIconAndImagesChangeFormState = Record<'valid' | 'invalid' | 'touched', boolean>;

export const WHITELABEL_IMAGES = [
  'logo',
  'logomarca',
  'webLoginBackground',
  'favIcon',
  'mobileLogin',
  'mobileIcon',
  'mobileSplash',
  'mobileNotification',
  'storeIcon',
  'storeBanner'
] as const;

@Component({
  selector: 'app-create-white-label-icon-and-images',
  templateUrl: './create-white-label-icon-and-images.component.html',
  styleUrls: ['./create-white-label-icon-and-images.component.scss']
})
export class CreateWhiteLabelIconAndImagesComponent implements OnInit, OnDestroy {
  @Input() whitelabelIdentification = 'whitelabel-sem-nome';
  @Input() initialImages: WhitelabelImages | null = null;

  @Output() changeImages = new EventEmitter<WhitelabelImages>();
  @Output() changeFormState = new EventEmitter<CreateWhitelabelIconAndImagesChangeFormState>();

  fieldsSections: Record<FieldsSections, FieldSectionConfig> = {
    [FieldsSections.WEB]: {
      title: 'Web',
      subtitle: 'Todas as imagens que aparecem na aplicação web (Roxo)'
    },
    [FieldsSections.MOBILE]: {
      title: 'Aplicativo móvel (mobile)',
      subtitle: 'Todas as imagens que compõem a aplicação móvel (ícone, splash, etc.)'
    },
    [FieldsSections.STORE]: {
      title: 'Lojas',
      subtitle: 'As imagens que são utilizadas nas lojas de aplicativos App Store (Apple) e Play Store (Android)'
    }
  };

  fields: Record<WhitelabelImageKey, FieldConfig> = {
    logo: {
      section: FieldsSections.WEB,
      label: 'Logo da tela de login',
      helpText: 'A proporção ideal para a logo é de 1:1 (quadrada), com resolução mínima de 500 x 500px.',
      originalImageBase64: null,
      imageFileName: 'logo',
      isCropped: false,
      autoGenerateImage: false,
      awsBucketImagePath: '',
      imageConfig: {
        container: { width: 1000, height: 1000 },
        image: { width: 1000, height: 1000 },
        hasTransparency: true
      }
    },
    logomarca: {
      section: FieldsSections.WEB,
      label: 'Logomarca para a barra superior',
      helpText: 'Caso essa imagem não seja fornecida, o sistema irá utilizar a <b>logo da tela de login</b>.',
      originalImageBase64: null,
      imageFileName: 'logomarca',
      isCropped: false,
      autoGenerateImage: true,
      awsBucketImagePath: '',
      imageConfig: {
        container: { width: 1000, height: 1000 },
        image: { width: 1000, height: 1000 },
        hasTransparency: true
      }
    },
    webLoginBackground: {
      section: FieldsSections.WEB,
      label: 'Imagem de fundo da tela de login',
      helpText: 'Caso essa imagem não seja fornecida, o sistema irá utilizar a padrão do sistema com a cor do white label.',
      originalImageBase64: null,
      imageFileName: 'web-login-background',
      isCropped: false,
      autoGenerateImage: false,
      awsBucketImagePath: '',
      imageConfig: {
        container: { width: 2600, height: 1600 },
        image: { width: 2600, height: 1600 },
        hasTransparency: true
      }
    },
    favIcon: {
      section: FieldsSections.WEB,
      label: 'Favicon',
      helpText: 'Imagem que fica na aba do navegador.',
      originalImageBase64: null,
      imageFileName: 'favIcon',
      isCropped: false,
      autoGenerateImage: true,
      awsBucketImagePath: '',
      imageConfig: {
        container: { width: 128, height: 128 },
        image: { width: 128, height: 128 },
        hasTransparency: true
      }
    },
    mobileLogin: {
      section: FieldsSections.MOBILE,
      label: 'Logo da tela de login',
      helpText: 'Caso essa imagem não seja fornecida, será usada a <b>logo da tela de login da web</b>, como já ocorre atualmente.',
      originalImageBase64: null,
      imageFileName: 'logo-login-mobile',
      isCropped: false,
      autoGenerateImage: true,
      awsBucketImagePath: '',
      imageConfig: {
        container: { width: 512, height: 512 },
        image: { width: 512, height: 512 },
        hasTransparency: true
      }
    },
    mobileIcon: {
      section: FieldsSections.MOBILE,
      label: 'Ícone',
      // helpText: '',
      originalImageBase64: null,
      imageFileName: 'icon',
      isCropped: false,
      autoGenerateImage: true,
      awsBucketImagePath: '',
      imageConfig: {
        container: { width: 1024, height: 1024 },
        image: { width: 768, height: 768 },
        hasTransparency: false
      }
    },
    mobileSplash: {
      section: FieldsSections.MOBILE,
      label: 'Splash',
      // helpText: 'A proporção ideal para a logo é de 1:1 (quadrada), com resolução mínima de 1000x1000.',
      originalImageBase64: null,
      imageFileName: 'splash',
      isCropped: false,
      autoGenerateImage: true,
      awsBucketImagePath: '',
      imageConfig: {
        container: { width: 2732, height: 2732 },
        hasTransparency: false
      }
    },
    mobileNotification: {
      section: FieldsSections.MOBILE,
      label: 'Ícone para notificação',
      helpText: 'A partir dessa imagem, todas os demais recursos <i>drawable-[mdpi,hdpi,xhdpi,xxhdpi,xxxhdpi]</i> serão gerados.',
      originalImageBase64: null,
      imageFileName: 'notification-icon',
      isCropped: false,
      autoGenerateImage: true,
      awsBucketImagePath: '',
      imageConfig: {
        container: { width: 1000, height: 1000 },
        hasTransparency: true
      },
      derivedImages: [
        {
          imageFileName: 'ic_stat_onesignal_default',
          imageFolder: 'notification-icons/drawable-mdpi',
          imageBase64: null,
          imageConfig: {
            container: { width: 48, height: 48 },
            image: { width: 48, height: 48 },
            hasTransparency: true
          }
        },
        {
          imageFileName: 'ic_stat_onesignal_default',
          imageFolder: 'notification-icons/drawable-hdpi',
          imageBase64: null,
          imageConfig: {
            container: { width: 24, height: 24 },
            image: { width: 24, height: 24 },
            hasTransparency: true
          }
        },
        {
          imageFileName: 'ic_stat_onesignal_default',
          imageFolder: 'notification-icons/drawable-xhdpi',
          imageBase64: null,
          imageConfig: {
            container: { width: 48, height: 48 },
            image: { width: 48, height: 48 },
            hasTransparency: true
          }
        },
        {
          imageFileName: 'ic_stat_onesignal_default',
          imageFolder: 'notification-icons/drawable-xxhdpi',
          imageBase64: null,
          imageConfig: {
            container: { width: 72, height: 72 },
            image: { width: 72, height: 72 },
            hasTransparency: true
          }
        },
        {
          imageFileName: 'ic_stat_onesignal_default',
          imageFolder: 'notification-icons/drawable-xxxhdpi',
          imageBase64: null,
          imageConfig: {
            container: { width: 512, height: 512 },
            image: { width: 512, height: 512 },
            hasTransparency: true
          }
        },
        {
          imageFileName: 'ic_onesignal_large_icon_default',
          imageFolder: 'notification-icons/drawable-xxxhdpi',
          imageBase64: null,
          imageConfig: {
            container: { width: 512, height: 512 },
            image: { width: 512, height: 512 },
            hasTransparency: true
          }
        }
      ]
    },
    storeIcon: {
      section: FieldsSections.STORE,
      label: 'Ícone',
      helpText:
        'O ícone do app precisa ser um PNG de até 1 MB e 512 × 512px, além de obedecer às <a href="https://developer.android.com/google-play/resources/icon-design-specifications" target="_blank">especificações de design</a>.',
      originalImageBase64: null,
      imageFileName: 'playstore-icon',
      isCropped: false,
      autoGenerateImage: true,
      awsBucketImagePath: '',
      imageConfig: {
        container: { width: 512, height: 512 },
        image: { width: 384, height: 384 },
        hasTransparency: true
      }
    },
    storeBanner: {
      section: FieldsSections.STORE,
      label: 'Banner',
      helpText: 'O banner precisa ser uma imagem PNG ou JPEG de até 15 MB e 1024px × 500px.',
      originalImageBase64: null,
      imageFileName: 'banner_playstore',
      isCropped: false,
      autoGenerateImage: true,
      awsBucketImagePath: '',
      imageConfig: {
        container: { width: 1024, height: 500 },
        image: { width: 424, height: 424 },
        hasTransparency: true
      }
    }
  };

  form = new UntypedFormGroup(
    Object.entries(this.fields).reduce(
      (acc, [fieldKey, field]) => {
        const fieldFormControl = new UntypedFormControl(null);

        if (field.imageConfig.container) {
          const imageSizeValidator = imageMaxSizeValidator({
            maxWidth: field.imageConfig.container.width,
            maxHeight: field.imageConfig.container.height
          });

          fieldFormControl.setAsyncValidators([imageSizeValidator]);
        }

        acc[fieldKey as WhitelabelImageKey] = fieldFormControl;

        return acc;
      },
      {} as Record<WhitelabelImageKey, UntypedFormControl>
    )
  );

  shouldGenerateOtherImagesBasedOnLogo = true;

  private unsubscribe$ = new Subject();

  unsortedKeyValue = unsortedKeyValue;

  constructor(
    private modalService: NzModalService,
    private messageService: NzMessageService
  ) {
    // Gera as demais imagens a partir da logo.
    // Só serão geradas as imagens para os campos que ainda não têm valor.
    this.form.controls.logo.valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(async (value: string | null) => {
      if (value && this.shouldGenerateOtherImagesBasedOnLogo) {
        const imagesKeys = Object.keys(this.fields).filter(key => key !== 'logo');

        const generateImagesPromises = imagesKeys
          // Só vai alterar os campos com autoGenerateImage=true e que ainda não têm imagem
          .filter(imageKey => {
            const { autoGenerateImage } = this.fields[imageKey as WhitelabelImageKey];
            const formControl = this.form.get(imageKey);
            return autoGenerateImage && !formControl?.value;
          })
          .map(async imageKey => {
            const field = this.fields[imageKey as WhitelabelImageKey];
            console.log(field.imageFileName, { imageConfig: field.imageConfig });
            const newImageBase64 = await generateImageFromAnother(value, field.imageConfig);
            await this.setImageFieldValue(imageKey as WhitelabelImageKey, { imageBase64: newImageBase64 });
          });

        await Promise.all(generateImagesPromises);
      }
    });

    // Toda vez que um campo com imagens derivadas é alterado, gera as imagens derivadas de novo.
    WHITELABEL_IMAGES.filter(fieldKey => !!this.fields[fieldKey].derivedImages?.length).forEach(fieldKey => {
      this.form.controls[fieldKey].valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(async value => {
        if (value) {
          await this.generateDerivedImages(fieldKey);
        }
      });
    });

    // A cada alteração em qualquer campo do formulário, emite o valor atualizado para fora.
    WHITELABEL_IMAGES.forEach(fieldKey => {
      this.form.controls[fieldKey].valueChanges.pipe(takeUntil(this.unsubscribe$)).subscribe(async () => {
        const whitelabelImages = this.buildWhitelabelImages();
        this.changeImages.emit(whitelabelImages);
      });
    });
  }

  ngOnInit() {
    if (this.initialImages) {
      Object.keys(this.initialImages).forEach(key => {
        const initialValue = this.initialImages?.[key as WhitelabelImageKey];

        this.setImageFieldValue(key as WhitelabelImageKey, {
          imageBase64: initialValue?.imageBase64 || null,
          originalImageBase64: initialValue?.originalImageBase64 || null,
          isCropped: initialValue?.isCropped,
          derivedImages: initialValue?.derivedImages,
          emitEvent: false
        });
      });
    }

    if (this.whitelabelIdentification) {
      WHITELABEL_IMAGES.forEach(imageKey => {
        const field = this.fields[imageKey];
        field.awsBucketImagePath = `${environment.awsBucketWhiteLabelBaseUrl}/${this.whitelabelIdentification}/${field.imageFileName}.png`;
      });
    }
  }

  private buildWhitelabelImages(): WhitelabelImages {
    return WHITELABEL_IMAGES.reduce((acc, cur) => {
      const field = this.fields[cur];
      const formControl = this.form.get(cur);

      acc[cur] = {
        imageBase64: formControl?.value,
        originalImageBase64: field.originalImageBase64,
        imageFileName: field.imageFileName,
        imageFolder: field.imageFolder || null,
        derivedImages: field.derivedImages || null,
        isCropped: field.isCropped
      };

      return acc;
    }, {} as WhitelabelImages);
  }

  async handlePickImage(fieldKey: WhitelabelImageKey) {
    const file = await pickFile({ acceptedFileTypes: ['image/png'] });

    let fileDataURL: string | null = null;

    if (file) {
      fileDataURL = await readFileAsDataURL(file);
    }

    await this.setImageFieldValue(fieldKey, { imageBase64: fileDataURL });
  }

  async handleDownloadImage(fieldKey: WhitelabelImageKey) {
    const field = this.fields[fieldKey];

    if (field.derivedImages?.length) {
      await this.handleDownloadImages({
        imagesToDownload: [fieldKey as WhitelabelImageKey],
        zipFileName: field.imageFolder
      });
    } else {
      const formControl = this.form.get(fieldKey);
      const imageBase64 = formControl?.value;

      const imageBlob = convertBase64ToBlob(imageBase64);

      // Criar um link de download
      const link = document.createElement('a');
      link.href = URL.createObjectURL(imageBlob);
      link.download = `${field.imageFileName}.png`;
      link.click();
    }
  }

  async handleDownloadImages({ imagesToDownload = [...WHITELABEL_IMAGES], zipFileName = this.whitelabelIdentification } = {}) {
    const [JSZip, { saveAs }] = await Promise.all([import('jszip'), import('file-saver')]);
    const zip = new JSZip();

    const existingImagesKeys = imagesToDownload.filter(key => {
      const formControl = this.form.get(key);
      return !!formControl?.value;
    });

    if (!existingImagesKeys.length) {
      this.messageService.error('Não há nenhuma imagem para baixar.');
      return;
    }

    existingImagesKeys.forEach(key => {
      const field = this.fields[key];

      const filePath = field.imageFolder ? `${field.imageFolder}/${field.imageFileName}.png` : `${field.imageFileName}.png`;

      const formControl = this.form.get(key);
      const fileBase64Data = convertBase64ToBlob(formControl?.value);

      const folder = zip.folder(zipFileName) || zip;
      folder.file(filePath, fileBase64Data, { createFolders: true });

      if (field.derivedImages?.length) {
        field.derivedImages
          .filter(image => !!image.imageBase64)
          .forEach(image => {
            let derivedImageFilePath = image.imageFolder ? `${image.imageFolder}/${image.imageFileName}.png` : `${image.imageFileName}.png`;

            if (field.imageFolder) {
              derivedImageFilePath = `${field.imageFolder}/${derivedImageFilePath}`;
            }

            const derivedImageBase64 = convertBase64ToBlob(image.imageBase64!);

            folder.file(derivedImageFilePath, derivedImageBase64, { createFolders: true });
          });
      }
    });

    await zip.generateAsync({ type: 'blob' }).then((content: Blob) => {
      saveAs(content, `${zipFileName}.zip`);
    });
  }

  async handleRemoveImage(fieldKey: WhitelabelImageKey) {
    await this.setImageFieldValue(fieldKey, {
      imageBase64: null,
      originalImageBase64: null,
      isCropped: false
    });

    const field = this.fields[fieldKey];

    if (field.derivedImages?.length) {
      field.derivedImages.forEach(derivedImage => {
        derivedImage.imageBase64 = null;
      });
    }
  }

  handleRemoveAllImages() {
    WHITELABEL_IMAGES.forEach(imageKey => {
      this.handleRemoveImage(imageKey);
    });
  }

  showModalImageCropper(fieldKey: WhitelabelImageKey) {
    const formControl = this.form.get(fieldKey);

    const modal: NzModalRef<ModalImageCropperComponent> = this.modalService.create({
      nzTitle: 'Cortar imagem',
      nzContent: ModalImageCropperComponent,
      nzData: { initialImageBase64: formControl?.value },
      nzWidth: 800,
      nzFooter: [
        {
          label: 'Cancelar',
          type: 'default',
          onClick: () => modal.destroy()
        },
        {
          label: 'Cortar',
          type: 'primary',
          onClick: async () => {
            const croppedImageBase64 = modal.componentInstance?.getCroppedImageBase64();

            await this.setImageFieldValue(fieldKey, {
              imageBase64: croppedImageBase64!,
              originalImageBase64: formControl?.value,
              isCropped: true
            });

            modal.destroy();
          }
        }
      ]
    });
  }

  async undoImageChanges(fieldKey: WhitelabelImageKey) {
    await this.setImageFieldValue(fieldKey, {
      imageBase64: this.fields[fieldKey].originalImageBase64,
      isCropped: false
    });
  }

  private async setImageFieldValue(
    fieldKey: WhitelabelImageKey,
    {
      imageBase64,
      originalImageBase64 = null,
      isCropped,
      derivedImages,
      emitEvent = true
    }: {
      imageBase64: string | null;
      originalImageBase64?: string | null;
      isCropped?: boolean;
      derivedImages?: DerivedImage[] | null;
      emitEvent?: boolean;
    }
  ) {
    const formControl = this.form.get(fieldKey);
    formControl?.setValue(imageBase64, { emitEvent });
    formControl?.markAsTouched();

    const field = this.fields[fieldKey];
    field.originalImageBase64 = originalImageBase64 || imageBase64;
    field.isCropped = isCropped ?? field.isCropped;

    if (derivedImages?.length) {
      field.derivedImages = derivedImages;
    } else {
      await this.generateDerivedImages(fieldKey);
    }

    this.changeFormState.emit({
      valid: this.form.valid,
      invalid: this.form.invalid,
      touched: this.form.touched
    });
  }

  private async generateDerivedImages(fieldKey: WhitelabelImageKey) {
    const formControl = this.form.get(fieldKey);
    const field = this.fields[fieldKey];

    if (formControl?.value && field.derivedImages?.length) {
      const derivedImagesPromises = field.derivedImages.map(async image => {
        image.imageBase64 = await generateImageFromAnother(formControl?.value, image.imageConfig);
      });

      await Promise.all(derivedImagesPromises);
    }
  }

  ngOnDestroy(): void {
    if (this.unsubscribe$) {
      this.unsubscribe$.next(null);
      this.unsubscribe$.complete();
    }
  }
}
